import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { h1, waLink } from "./ui";
import { ArrowLeft, Mail, Phone, ClipboardList, Activity, Paperclip, Sparkles, Loader2, MessageCircle, Wallet } from "lucide-react";
import ClinicalData from "./prontuario/ClinicalData";
import Evolutions from "./prontuario/Evolutions";
import Attachments from "./prontuario/Attachments";
import PatientChat from "./prontuario/PatientChat";
import PatientFinance from "./prontuario/PatientFinance";

export default function Prontuario() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const initialTab = ["clinico", "evolucao", "financeiro", "anexos", "ia"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "clinico";
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get(`/patients/${id}`).then(({ data }) => setPatient(data)).catch(() => setPatient(null)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#5B6E5B]" /></div>;
  if (!patient) return (
    <div data-testid="prontuario-notfound">
      <Link to="/dashboard/pacientes" className="inline-flex items-center gap-2 text-sm text-[#5B6E5B] hover:text-[#2C332A]"><ArrowLeft className="w-4 h-4" /> Voltar</Link>
      <p className="font-body text-sm text-[#7A8A7A] mt-6">Paciente não encontrado.</p>
    </div>
  );

  return (
    <div data-testid="prontuario-page">
      <Link to="/dashboard/pacientes" data-testid="prontuario-back" className="inline-flex items-center gap-2 text-sm text-[#5B6E5B] hover:text-[#2C332A] transition-colors">
        <ArrowLeft className="w-4 h-4" /> Voltar aos pacientes
      </Link>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-body text-xs tracking-[0.35em] uppercase text-[#C87A5E]">Prontuário</p>
          <h1 className={h1}>{patient.name}</h1>
          <div className="flex flex-wrap gap-4 mt-2 items-center">
            {patient.email && <span className="font-body text-sm text-[#7A8A7A] flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {patient.email}</span>}
            {patient.phone && <span className="font-body text-sm text-[#7A8A7A] flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {patient.phone}</span>}
            {patient.phone && (
              <a href={waLink(patient.phone)} target="_blank" rel="noreferrer" data-testid="prontuario-whatsapp"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366]/12 px-3 py-1 text-xs text-[#128C4A] hover:bg-[#25D366]/20 transition-colors">
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </a>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue={initialTab} className="mt-8">
        <TabsList className="flex flex-wrap gap-2 bg-transparent p-0 h-auto">
          <TabsTrigger value="clinico" data-testid="tab-clinico" className="gap-2 data-[state=active]:bg-[#5B6E5B] data-[state=active]:text-[#FDFBF7] rounded-full px-4 py-2 border border-[#5B6E5B]/20"><ClipboardList className="w-4 h-4" /> Dados clínicos</TabsTrigger>
          <TabsTrigger value="evolucao" data-testid="tab-evolucao" className="gap-2 data-[state=active]:bg-[#5B6E5B] data-[state=active]:text-[#FDFBF7] rounded-full px-4 py-2 border border-[#5B6E5B]/20"><Activity className="w-4 h-4" /> Evolução</TabsTrigger>
          <TabsTrigger value="financeiro" data-testid="tab-financeiro" className="gap-2 data-[state=active]:bg-[#5B6E5B] data-[state=active]:text-[#FDFBF7] rounded-full px-4 py-2 border border-[#5B6E5B]/20"><Wallet className="w-4 h-4" /> Financeiro</TabsTrigger>
          <TabsTrigger value="anexos" data-testid="tab-anexos" className="gap-2 data-[state=active]:bg-[#5B6E5B] data-[state=active]:text-[#FDFBF7] rounded-full px-4 py-2 border border-[#5B6E5B]/20"><Paperclip className="w-4 h-4" /> Anexos</TabsTrigger>
          <TabsTrigger value="ia" data-testid="tab-ia" className="gap-2 data-[state=active]:bg-[#5B6E5B] data-[state=active]:text-[#FDFBF7] rounded-full px-4 py-2 border border-[#5B6E5B]/20"><Sparkles className="w-4 h-4" /> Assistente IA</TabsTrigger>
        </TabsList>

        <TabsContent value="clinico" className="mt-6"><ClinicalData patient={patient} onSaved={load} /></TabsContent>
        <TabsContent value="evolucao" className="mt-6"><Evolutions patientId={id} /></TabsContent>
        <TabsContent value="financeiro" className="mt-6"><PatientFinance patientId={id} /></TabsContent>
        <TabsContent value="anexos" className="mt-6"><Attachments patientId={id} /></TabsContent>
        <TabsContent value="ia" className="mt-6"><PatientChat patientId={id} patientName={patient.name} /></TabsContent>
      </Tabs>
    </div>
  );
}
